import { easeShare, GHOST_POWER_BP, CRYPT_ASCUAS, LOOT_POWER, DEEP_CRYPT, DUNGEON, DUNGEON_REWARD, INCURSION_OMENS, NIGHTMARES, RELIC_DROPS, WARDEN, type NightmareType, TOMES, GLYPH_FRAGS } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { hexDist, hexRing } from '../hex.ts';
import { drawPuzzles } from '../battle/deep.ts';
import { createDeepCryptMap, createDungeonMap } from '../map.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng } from '../math/rng.ts';
import { snapshot } from '../math/hash.ts';
import type { BattleState, DungeonSpawn } from '../types.ts';
import { groupReached, withDelves } from './eras.ts';
import { bigParty, crowd } from './party.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { dayOmen, omenRules, openSites, rollRelic } from './incursion.ts';
import { giveItem, grantSeal, lootCtx, rollItem } from './items.ts';
import { afflictAfter, fightKit, grantXp, addGlyphFragment, addPages } from './progress.ts';
import type { CampaignState, Ms } from './types.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats } from './feats.ts';
import { logOuting } from './outings.ts';
import { simaRecord } from './sima.ts';

/** Warlord dens: reaching one opens the crypt. */
const DENS = [12, 15];

/** The crypt opens once the group has reached a warlord's den at the titan's head or tail. */
export const dungeonOpen = (s: CampaignState): boolean => openSites(s).some((id) => DENS.includes(id));

/** The crypt in embers (level II): from group level 5, once the group has won the crypt (someone took its chest). */
export const cryptTierOpen = (s: CampaignState): boolean =>
  dungeonOpen(s) && groupReached(s, CRYPT_ASCUAS.minGroup) && s.players.some((p) => p.dungeonDay !== undefined);

/** The deep crypt (level III): from group level 8, once the group has won the crypt in embers. */
export const deepCryptOpen = (s: CampaignState): boolean =>
  dungeonOpen(s) && groupReached(s, DEEP_CRYPT.minGroup) && s.players.some((p) => p.dungeonDay2 !== undefined);

/**
 * Party scaling: by party size (steeper in embers), and by average level above 1 (DUNGEON's per-level numbers, or the
 * deep crypt's own). A party past four gets part of it as tougher nightmares (`hpBp`, BIG_PARTY). On top, every
 * nightmare's health and blows (`tough…`), more for each artificer in the party (`artificer…`).
 */
const scale = (
  s: CampaignState,
  pids: string[],
  deep: boolean,
  table?: readonly number[],
  by: { perLevelBp: number; fromLevel: number; countPerLevelBp: number; hpPerLevelBp: number; dmgPerLevelBp: number; toughHpBp: number; toughDmgBp: number; easedHpBp: number; easedDmgBp: number; artificerHpBp: number; artificerDmgBp: number } = DUNGEON,
): { all: number; level: number; dmg: number; hpBp: number; ease: number } => {
  // An empty lobby is laid out as for one hero; it is rebuilt as heroes arrive.
  const n = pids.length || 1;
  let lv = 0;
  let artificers = 0;
  for (const pid of pids) {
    const hero = findPlayer(s, pid)?.hero;
    lv += hero?.level ?? 1;
    if (hero?.heroClass === 'artificer') artificers++;
  }
  const above = idiv(lv * 100, n || 1) - 100;
  // Up to `fromLevel` every level brings more nightmares that hit harder; past it, fewer more but tougher and fiercer ones.
  const lo = imin(above, (by.fromLevel - 1) * 100);
  const hi = imax(0, above - lo);
  const level = 10000 + idiv(above * by.perLevelBp, 100);
  const dmg = 10000 + idiv(lo * by.perLevelBp + hi * by.dmgPerLevelBp, 100);
  const count = 10000 + idiv(lo * by.perLevelBp + hi * by.countPerLevelBp, 100);
  const hp = 10000 + idiv(hi * by.hpPerLevelBp, 100);
  const party = crowd(table ?? (deep ? CRYPT_ASCUAS : DUNGEON).partyBp, n);
  // From the level the unique armour used to come at, eased: the armour and amulet come later now (docs/UNICAS.md §5).
  // It eases over `rampLevels` levels (0 at `fromLevel` - 1, whole at `fromLevel` + rampLevels - 1).
  const ease = easeShare(above);
  const toughHp = by.toughHpBp + idiv((by.easedHpBp - by.toughHpBp) * ease, 10000) + artificers * by.artificerHpBp;
  const toughDmg = by.toughDmgBp + idiv((by.easedDmgBp - by.toughDmgBp) * ease, 10000) + artificers * by.artificerDmgBp;
  return { all: mulBp(party.count, count), level, dmg: mulBp(dmg, toughDmg), hpBp: mulBp(mulBp(party.hpBp, hp), toughHp), ease };
};

/**
 * Who goes down: the live heroes, then the absent friends brought as spectres (`ghosts`; one who plays live is not
 * also a spectre), up to the crypt's size. The crypt is filled for all of them: a spectre counts as a whole hero.
 */
const goingDown = (s: CampaignState, pids: string[], ghosts: string[]): { live: string[]; spectres: string[]; party: string[] } => {
  const live = [...new Set(pids)].filter((pid) => !!findPlayer(s, pid)).sort().slice(0, DUNGEON.maxHeroes);
  const spectres = [...new Set(ghosts)]
    .filter((pid) => !live.includes(pid) && !!findPlayer(s, pid) && !findPlayer(s, pid)?.left)
    .sort()
    .slice(0, DUNGEON.maxHeroes - live.length);
  return { live, spectres, party: [...live, ...spectres] };
};

/**
 * Today's crypt for a party (1 to 8 heroes, live, and absent friends' spectres): the same halls for the whole group
 * all day, filled to the party's size and level.
 */
export const dungeonParams = (s: CampaignState, pids: string[], day: number, tier: 1 | 2 | 3 = 1, ghosts: string[] = []): BattleParams | null => {
  if (tier === 2 && !cryptTierOpen(s)) return null;
  if (tier === 3) return deepCryptOpen(s) ? deepCryptParams(s, pids, day, ghosts) : null;
  const { live, spectres, party } = goingDown(s, pids, ghosts);
  // The crypt in embers: its own halls for the day, longer, and its own bestiary.
  const deep = tier === 2;
  const rooms = deep ? CRYPT_ASCUAS.rooms : DUNGEON.rooms;
  const bestiary = deep ? CRYPT_ASCUAS.bestiary : DUNGEON.bestiary;
  const roomPoints = deep ? CRYPT_ASCUAS.roomPoints : DUNGEON.roomPoints;
  const inks = deep ? CRYPT_ASCUAS.inkBp : DUNGEON.inkBp;
  const seed = deriveSeed(s.seed, (deep ? 13000 : 12000) + day);
  const map = createDungeonMap(seed, rooms);
  const L = map.dungeon;
  if (!L) return null;
  const rng = seedRng(seed ^ 0xc4b7);
  const k = scale(s, party, deep);
  // The crypt in embers' toughness is applied in the fight (`CRYPT_ASCUAS.enemyHpBp`); eased, its share goes in the rules.
  const ease = deep && k.ease > 0 ? idiv((CRYPT_ASCUAS.enemyHpBp + idiv((CRYPT_ASCUAS.easedHpBp - CRYPT_ASCUAS.enemyHpBp) * k.ease, 10000)) * 10000, CRYPT_ASCUAS.enemyHpBp) : 10000;
  // The day's omen goes down into the crypt too: more ink, a bigger and frailer swarm, faster or blinder.
  const omenId = dayOmen(s, day);
  const omen = INCURSION_OMENS[omenId];
  const spawns: DungeonSpawn[] = [];
  for (let i = 1; i <= rooms; i++) {
    const room = L.rooms[i];
    const door = L.doors[i - 1];
    if (!room || !door) continue;
    const beasts = withDelves(s, bestiary[i - 1] ?? {});
    const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
    // Spots in the room, away from the door the party comes in by.
    const spots: { q: number; r: number }[] = [];
    for (let rr = 1; rr < room.radius; rr++) for (const h of hexRing(rr)) spots.push({ q: room.q + h.q, r: room.r + h.r });
    const far = spots.filter((h) => hexDist(h, door) >= 3);
    const ink = imin(10000, (inks[i - 1] ?? 0) + (omen.inkBp ?? 0));
    for (let w = 0; w < DUNGEON.wavesPerRoom; w++) {
      let left = mulBp(mulBp(roomPoints[i - 1] ?? 30, k.all), omen.budgetBp ?? 10000);
      let guard = 0;
      while (left > 0 && guard++ < 60) {
        const fit = entries.filter(([t]) => pointsOf(t) <= left);
        if (!fit.length) break;
        const kind = rngWeighted(rng, fit);
        left -= pointsOf(kind);
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: w, kind, tier: rngChance(rng, ink) ? 2 : 1, elite: false, q: at.q, r: at.r });
      }
    }
    if (i === rooms) {
      const elites = (deep ? CRYPT_ASCUAS.lastRoomElites : DUNGEON.lastRoomElites) + idiv(party.length - 1, 2);
      for (let e = 0; e < elites; e++) {
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: DUNGEON.wavesPerRoom - 1, kind: 'skeleton', tier: 2, elite: true, q: at.q, r: at.r });
      }
    }
  }
  const heroes = partyHeroes(s, live, spectres);
  return snapshot({
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes,
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns, bossHp: mulBp(mulBp(bigParty(WARDEN.hpByParty, party.length || 1), k.level), deep ? (CRYPT_ASCUAS.bossHpBp[imin(party.length || 1, CRYPT_ASCUAS.bossHpBp.length - 1)] as number) : 10000), dmgBp: deep ? mulBp(k.dmg, CRYPT_ASCUAS.dmgBp + idiv((CRYPT_ASCUAS.easedDmgBp - CRYPT_ASCUAS.dmgBp) * k.ease, 10000)) : k.dmg },
    rules: {
      omen: omenId,
      ...(deep ? { tier: 2 as const } : {}),
      ...(omen.enemyHpBp || k.hpBp !== 10000 || ease !== 10000 ? { enemyHpBp: mulBp(mulBp(omen.enemyHpBp ?? 10000, k.hpBp), ease) } : {}),
      ...(omen.enemySpeedBp ? { enemySpeedBp: omen.enemySpeedBp } : {}),
      ...(omen.heroRangeBp ? { heroRangeBp: omen.heroRangeBp } : {}),
      ...omenRules({}, omen),
      ...(spectres.length ? { spectres } : {}),
    },
  });
};

const pointsOf = (t: NightmareType): number => NIGHTMARES[t].points;

/** A party's heroes, as the crypt takes them: the live ones first, then the spectres (at a ghost's strength, playing as their players do). */
const partyHeroes = (s: CampaignState, live: string[], spectres: string[] = []): BattleParams['heroes'] =>
  [...live, ...spectres].map((pid, i) => {
    const p = findPlayer(s, pid) as NonNullable<ReturnType<typeof findPlayer>>;
    const ghost = i >= live.length;
    return {
      pid,
      name: heroName(p),
      cls: p.hero.heroClass,
      level: p.hero.level,
      control: ghost ? 'ghost' : 'live',
      powerBp: ghost ? GHOST_POWER_BP : 10000,
      post: i,
      ...(ghost ? { profile: p.ghostProfile } : {}),
      relics: p.hero.relics,
      awakened: p.hero.awakened ?? [],
      ...fightKit(p.hero, 'crypt', s),
    };
  });

/**
 * The deep crypt for a party: three floors (stone, embers, obsidian), each with a fighting room, a puzzle
 * room and a guardian. Unlike the other crypts, every descent is new: its seed counts the runs.
 */
const deepCryptParams = (s: CampaignState, pids: string[], day: number, ghosts: string[]): BattleParams | null => {
  const { live, spectres, party } = goingDown(s, pids, ghosts);
  const seed = deriveSeed(s.seed, 14000 + day * 64 + ((s.deepRuns ?? 0) % 64));
  const puzzles = drawPuzzles(seedRng(seed ^ 0x9a22), party.length);
  const map = createDeepCryptMap(seed, DEEP_CRYPT.floors, puzzles);
  const L = map.dungeon;
  if (!L) return null;
  const rng = seedRng(seed ^ 0xc4b8);
  const k = scale(s, party, true, DEEP_CRYPT.partyBp, DEEP_CRYPT);
  const n = party.length || 1;
  const spawns: DungeonSpawn[] = [];
  L.rooms.forEach((room, i) => {
    const door = L.doors[i - 1];
    if (room.kind !== 'room' || !door) return;
    const f = room.floor ?? 0;
    const glass = room.puzzle === 'glass';
    const beasts = glass ? withDelves(s, DEEP_CRYPT.flyers, true) : withDelves(s, DEEP_CRYPT.bestiary[f] ?? {});
    const entries = (Object.keys(beasts) as NightmareType[]).sort().map((t) => [t, beasts[t] as number] as const);
    const spots: { q: number; r: number }[] = [];
    for (let rr = 1; rr < room.radius; rr++) for (const h of hexRing(rr)) spots.push({ q: room.q + h.q, r: room.r + h.r });
    const far = spots.filter((h) => hexDist(h, door) >= 3);
    const pts = (DEEP_CRYPT.roomPoints[f] ?? [20, 12])[room.puzzle ? 1 : 0] as number;
    for (let w = 0; w < DUNGEON.wavesPerRoom; w++) {
      let left = mulBp(pts, k.all);
      let guard = 0;
      while (left > 0 && guard++ < 60) {
        const fit = entries.filter(([t]) => pointsOf(t) <= left);
        if (!fit.length) break;
        const kind = rngWeighted(rng, fit);
        left -= pointsOf(kind);
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: w, kind, tier: rngChance(rng, DEEP_CRYPT.inkBp[f] ?? 0) ? 2 : 1, elite: false, q: at.q, r: at.r });
      }
    }
    // The fighting room before each guardian: elites, more the deeper and the bigger the party.
    if (!room.puzzle) {
      for (let e = 0; e < f + idiv(n - 1, 2); e++) {
        const at = far[rngInt(rng, far.length)] as { q: number; r: number };
        spawns.push({ room: i, wave: DUNGEON.wavesPerRoom - 1, kind: 'skeleton', tier: 2, elite: true, q: at.q, r: at.r });
      }
    }
  });
  const guardianHp = DEEP_CRYPT.guardianHp.map((t) => mulBp(bigParty(t, n), k.level));
  return snapshot({
    mode: 'dungeon',
    seed,
    map,
    structures: [],
    heroes: partyHeroes(s, live, spectres),
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [],
    dungeon: { spawns, bossHp: guardianHp[2] as number, dmgBp: k.dmg, guardianHp },
    rules: { tier: 3, ...(k.hpBp !== 10000 ? { enemyHpBp: k.hpBp } : {}), ...(spectres.length ? { spectres } : {}) },
  });
};

/** The deep crypt's chest so far: what each floor cleared adds (falling keeps half). */
export const deepChest = (floors: number, fell: boolean): { xp: number; dust: number; shards: number; embers: number } => {
  const c = { xp: 0, dust: 0, shards: 0, embers: 0 };
  for (let f = 0; f < floors; f++) {
    const x = DEEP_CRYPT.chest[f];
    if (!x) continue;
    c.xp += x.xp;
    c.dust += x.dust;
    c.shards += x.shards;
    c.embers += x.embers;
  }
  if (!fell) return c;
  return { xp: mulBp(c.xp, DEEP_CRYPT.fallBp), dust: mulBp(c.dust, DEEP_CRYPT.fallBp), shards: mulBp(c.shards, DEEP_CRYPT.fallBp), embers: mulBp(c.embers, DEEP_CRYPT.fallBp) };
};

/**
 * A finished descent: each hero takes the chest of the floors cleared (half if the party fell). The day's
 * piece (from the second floor) and relic shot go once a day per hero; later descents give the rest.
 */
const applyDeepCrypt = (s: CampaignState, battle: BattleState, day: number, now: Ms): DungeonResult => {
  s.deepRuns = (s.deepRuns ?? 0) + 1;
  const floors = battle.dungeon?.deep?.cleared ?? 0;
  const fell = battle.result !== 'won';
  const chest = deepChest(floors, fell);
  const rewarded: string[] = [];
  const pids = liveOf(battle);
  for (const pid of pids) {
    const p = findPlayer(s, pid);
    if (!p) continue;
    grantXp(s, pid, chest.xp, now);
    if (chest.shards) p.hero.shards = (p.hero.shards ?? 0) + chest.shards;
    if (chest.embers) p.hero.embers = (p.hero.embers ?? 0) + chest.embers;
    if (floors > 0 && p.dungeonDay3 !== day) {
      p.dungeonDay3 = day;
      rewarded.push(pid);
      if (floors >= DEEP_CRYPT.pieceFrom) giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, LOOT_POWER.deepCrypt + floors * LOOT_POWER.deepFloor, 'deepCrypt', day)), now);
      const bp = DEEP_CRYPT.relicBp[imin(floors, DEEP_CRYPT.relicBp.length) - 1] ?? 0;
      if (bp && !fell) rollRelic(s, pid, bp, now);
      if (!fell) {
        addPages(s, pid, 0, TOMES.cryptHard, now);
      addGlyphFragment(s, pid, 'crypt', GLYPH_FRAGS.cryptHard, now);
        addGlyphFragment(s, pid, 'crypt', GLYPH_FRAGS.cryptHard, now);
      }
    }
    // The deep crypt won to its bottom: the Chasm under it opens (docs/SIMA.md).
    if (!fell && floors >= DEEP_CRYPT.floors) s.sima = { ...simaRecord(s, day), open: true };
    if (!fell && floors >= DEEP_CRYPT.floors) {
      rollRelic(s, pid, RELIC_DROPS.colossus, now);
      // The deep crypt cleared to its last floor: the seal of a unique's first awakening.
      grantSeal(s, p, 'deep', now);
    }
  }
  carrySpectres(s, battle, chest.xp, now);
  if (chest.dust) addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: chest.dust });
  logOuting(s, now, 'crypt3', pids, !fell, floors);
  event(s, now, 'dungeon', { data: { won: !fell, rooms: floors, floors, tier: 3, party: pids.join(',') } });
  s.version++;
  return { rewarded, rooms: floors };
};

/** The heroes who went down live (the spectres' players were not there: they take only a share of the XP). */
const liveOf = (battle: BattleState): string[] => {
  const sp = battle.rules?.spectres ?? [];
  return battle.heroes.map((h) => h.pid).filter((pid) => !sp.includes(pid));
};

/** Each spectre's player gets `DUNGEON.spectreShareBp` of the XP a live hero took for the run, and the log says by whom. */
const carrySpectres = (s: CampaignState, battle: BattleState, xp: number, now: Ms): void => {
  const by = liveOf(battle)[0];
  for (const pid of battle.rules?.spectres ?? []) {
    const c = findPlayer(s, pid);
    if (!c) continue;
    const share = mulBp(xp, DUNGEON.spectreShareBp);
    grantXp(s, c.id, share, now);
    if (share > 0 && by) event(s, now, 'carried', { pid: c.id, data: { by, xp: share } });
  }
};

export interface DungeonResult {
  /** Heroes who took the day's reward. */
  rewarded: string[];
  rooms: number;
}

/**
 * Applies a finished crypt run to the campaign. A win gives each hero, once a day, a rare piece,
 * dream dust, a lot of XP and a chance at a relic, plus shared materials; other runs give XP per room.
 */
export const applyDungeon = (s: CampaignState, battle: BattleState, day: number, now: Ms, tier: 1 | 2 | 3 = 1): DungeonResult => {
  tallyDeeds(s, 'crypt', battle, now);
  // The deep crypt is one of the hard places (docs/UNICAS.md §2.2).
  tallyFeats(s, battle, { hard: tier === 3 }, now);
  afflictAfter(s, battle, now);
  if (tier === 2) return applyCryptAscuas(s, battle, day, now);
  if (tier === 3) return applyDeepCrypt(s, battle, day, now);
  const won = battle.result === 'won';
  const rooms = battle.dungeon ? battle.dungeon.cleared.filter((c, i) => c && i > 0).length + (won ? 1 : 0) : 0;
  const rewarded: string[] = [];
  const pids = liveOf(battle);
  for (const pid of pids) {
    const p = findPlayer(s, pid);
    if (!p) continue;
    if (won && p.dungeonDay !== day) {
      p.dungeonDay = day;
      rewarded.push(pid);
      giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, LOOT_POWER.crypt, 'crypt', day)), now);
      addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: DUNGEON_REWARD.dust });
      grantXp(s, pid, DUNGEON_REWARD.xp, now);
      rollRelic(s, pid, DUNGEON_REWARD.relicBp, now);
      addPages(s, pid, 0, TOMES.crypt, now);
      addGlyphFragment(s, pid, 'crypt', GLYPH_FRAGS.crypt, now);
    } else grantXp(s, pid, rooms * DUNGEON_REWARD.xpPerRoom, now);
    // Beating the Warden: a chance at a relic for each hero, every run (the chest's is apart).
    if (won) rollRelic(s, pid, RELIC_DROPS.warden, now);
  }
  carrySpectres(s, battle, won ? DUNGEON_REWARD.xp : rooms * DUNGEON_REWARD.xpPerRoom, now);
  if (rewarded.length) addRes(s.storage, { wood: DUNGEON_REWARD.res, stone: DUNGEON_REWARD.res, crystal: imin(DUNGEON_REWARD.res, 40), dust: 0 });
  logOuting(s, now, 'crypt1', pids, won);
  event(s, now, 'dungeon', { data: { won, rooms, party: pids.join(',') } });
  s.version++;
  return { rewarded, rooms };
};

/** The crypt in embers' own chest, once a day per hero: a rare piece, embers, dust, XP and a better shot at a relic. */
const applyCryptAscuas = (s: CampaignState, battle: BattleState, day: number, now: Ms): DungeonResult => {
  const won = battle.result === 'won';
  const C = CRYPT_ASCUAS;
  const rooms = battle.dungeon ? battle.dungeon.cleared.filter((c, i) => c && i > 0).length + (won ? 1 : 0) : 0;
  const rewarded: string[] = [];
  const pids = liveOf(battle);
  for (const pid of pids) {
    const p = findPlayer(s, pid);
    if (!p) continue;
    if (won && p.dungeonDay2 !== day) {
      p.dungeonDay2 = day;
      rewarded.push(pid);
      giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, LOOT_POWER.cryptAscuas, 'crypt', day)), now);
      p.hero.embers = (p.hero.embers ?? 0) + C.embers;
      addRes(s.storage, { wood: 0, stone: 0, crystal: 0, dust: C.dust });
      grantXp(s, pid, C.xp, now);
      rollRelic(s, pid, C.relicBp, now);
      addPages(s, pid, 0, TOMES.cryptHard, now);
      addGlyphFragment(s, pid, 'crypt', GLYPH_FRAGS.cryptHard, now);
    } else grantXp(s, pid, rooms * DUNGEON_REWARD.xpPerRoom, now);
    if (won) rollRelic(s, pid, RELIC_DROPS.wardenEmbers, now);
  }
  carrySpectres(s, battle, won ? C.xp : rooms * DUNGEON_REWARD.xpPerRoom, now);
  if (rewarded.length) addRes(s.storage, { wood: DUNGEON_REWARD.res, stone: DUNGEON_REWARD.res, crystal: imin(DUNGEON_REWARD.res, 40), dust: 0 });
  logOuting(s, now, 'crypt2', pids, won);
  event(s, now, 'dungeon', { data: { won, rooms, tier: 2, party: pids.join(',') } });
  s.version++;
  return { rewarded, rooms };
};
